import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './auth.jsx';

export const WORKSPACE_VIEWS = ['hr', 'admin'];

export const WORKSPACE_VIEW_LABELS = {
  management: 'Management',
  hr: 'HR',
  admin: 'Admin',
};

/** Nav item ids kept when the Admin tab is active (CRM-style scoped chrome). */
export const ADMIN_VIEW_NAV_IDS = new Set([
  'dashboard',
  'me',
  'attendance',
  'biometric',
  'announcements',
  'reports',
  'audit',
  'security',
  'settings',
]);

const ADMIN_PATH_PREFIXES = [
  '/',
  '/me',
  '/attendance',
  '/biometric',
  '/announcements',
  '/reports',
  '/audit',
  '/security',
  '/settings',
];

function storageKey(userId) {
  return `peopleosWorkspaceView:${userId}`;
}

export function canSwitchWorkspaceViews(user) {
  if (!user) return false;
  // ADMIN (and founders holding ADMIN) get both desks — same idea as CRM org admins.
  return user.role === 'ADMIN';
}

export function availableWorkspaceViews(user) {
  if (!canSwitchWorkspaceViews(user)) return ['hr'];
  // Founders / CXO (level 0) also keep the leadership board as a third tab.
  if (user.level === 0) return ['management', 'hr', 'admin'];
  return [...WORKSPACE_VIEWS];
}

export function pathAllowedForView(pathname, view) {
  if (view !== 'admin') return true;
  if (pathname === '/') return true;
  return ADMIN_PATH_PREFIXES.some((p) => p !== '/' && (pathname === p || pathname.startsWith(`${p}/`)));
}

const WorkspaceViewContext = createContext(null);

export function WorkspaceViewProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canSwitch = canSwitchWorkspaceViews(user);
  const views = availableWorkspaceViews(user);

  const [workspaceView, setWorkspaceViewState] = useState(() => {
    if (!user?.id || !canSwitchWorkspaceViews(user)) return 'hr';
    const allowed = availableWorkspaceViews(user);
    try {
      const saved = localStorage.getItem(storageKey(user.id));
      if (allowed.includes(saved)) return saved;
    } catch { /* ignore */ }
    return allowed.includes('management') ? 'management' : 'hr';
  });

  // Reset / rehydrate when the signed-in account changes (including view-as).
  useEffect(() => {
    if (!user?.id) return;
    const allowed = availableWorkspaceViews(user);
    if (!canSwitchWorkspaceViews(user)) {
      setWorkspaceViewState('hr');
      return;
    }
    try {
      const saved = localStorage.getItem(storageKey(user.id));
      setWorkspaceViewState(allowed.includes(saved) ? saved : (allowed.includes('management') ? 'management' : 'hr'));
    } catch {
      setWorkspaceViewState(allowed.includes('management') ? 'management' : 'hr');
    }
  }, [user?.id, user?.role, user?.level]);

  const setWorkspaceView = useCallback((next) => {
    const allowed = availableWorkspaceViews(user);
    if (!allowed.includes(next)) return;
    setWorkspaceViewState(next);
    if (user?.id) {
      try { localStorage.setItem(storageKey(user.id), next); } catch { /* ignore */ }
    }
  }, [user]);

  // If Admin tab cannot open the current page, land on the dashboard.
  useEffect(() => {
    if (!canSwitch) return;
    if (!pathAllowedForView(location.pathname, workspaceView)) {
      navigate('/', { replace: true });
    }
  }, [canSwitch, workspaceView, location.pathname, navigate]);

  const value = useMemo(() => ({
    workspaceView: canSwitch ? workspaceView : 'hr',
    setWorkspaceView,
    canSwitchWorkspaceView: canSwitch,
    availableViews: canSwitch ? views : ['hr'],
  }), [canSwitch, workspaceView, setWorkspaceView, views]);

  return (
    <WorkspaceViewContext.Provider value={value}>
      {children}
    </WorkspaceViewContext.Provider>
  );
}

export function useWorkspaceView() {
  const ctx = useContext(WorkspaceViewContext);
  if (!ctx) {
    return {
      workspaceView: 'hr',
      setWorkspaceView: () => {},
      canSwitchWorkspaceView: false,
      availableViews: ['hr'],
    };
  }
  return ctx;
}
